/**
 * Overview widgets that both portals show (HO across all stores, the store for itself): sales today, orders by
 * status, sales by hour, the last seven days and payments by provider. Each one owns its query, so a failing call
 * only affects its own card. Amounts come from the backend (system rule 5); permission checks are the page's job.
 */
import {
  Tag,
  Toggletip,
  ToggletipButton,
  ToggletipContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMemo } from 'react';
import { useFormat } from '../orders/model';
import { OrderStatusTag } from '../orders/OrderStatusTag';
import { useOverviewApi, useOverviewQuery, useSalesSummary, type OverviewMode } from './hooks';
import { todayDate, todayRange } from './overviewApi';
import { StatTile } from './StatTile';
import { TrendChart } from './TrendChart';
import type { SalesSeriesDto } from './types';
import { WidgetCard } from './WidgetCard';
import './overview.scss';

const MINUTE = 60_000;

/** Why HO figures can trail the stores (ADR 0015): a gray Tag with a Toggletip that explains it. */
export function LagNotice() {
  const { t } = useTranslation();
  return (
    <>
      <Tag type="gray">{t('admin.overview.lag.label')}</Tag>
      <Toggletip align="bottom-end">
        <ToggletipButton label={t('admin.overview.lag.about')} />
        <ToggletipContent>
          <p>{t('admin.overview.lag.help')}</p>
        </ToggletipContent>
      </Toggletip>
    </>
  );
}

export function SalesTodayWidget({ mode }: { mode: OverviewMode }) {
  const { t } = useTranslation();
  const format = useFormat();
  const query = useSalesSummary(mode);
  const s = query.data;
  const money = (v: number) => (s ? `${format.money(v, s.currencyCode)} ${s.currencyCode}` : '');
  return (
    <WidgetCard
      title={t('admin.overview.sales.title')}
      headerExtra={mode === 'ho' ? <LagNotice /> : undefined}
      isLoading={query.isPending}
      isError={query.isError}
      error={query.error}
      onRetry={() => void query.refetch()}
      isEmpty={!!s && s.paidOrders === 0 && (s.openOrders ?? 0) === 0}
      emptyHeading={t('admin.overview.sales.empty')}
    >
      {s && (
        <div className="pos-overview__stats">
          <StatTile label={t('admin.overview.sales.orders')} value={format.number(s.paidOrders)} />
          <StatTile label={t('admin.overview.sales.netSales')} value={money(s.netSales)} />
          <StatTile label={t('admin.overview.sales.averageTicket')} value={money(s.averageOrderValue)} />
          {s.openOrders != null && (
            <StatTile label={t('admin.overview.sales.openOrders')} value={format.number(s.openOrders)} />
          )}
        </div>
      )}
    </WidgetCard>
  );
}

export function OrdersByStatusWidget({ mode }: { mode: OverviewMode }) {
  const { t } = useTranslation();
  const format = useFormat();
  const query = useSalesSummary(mode);
  const rows = query.data?.ordersByStatus ?? [];
  return (
    <WidgetCard
      title={t('admin.overview.status.title')}
      isLoading={query.isPending}
      isError={query.isError}
      error={query.error}
      onRetry={() => void query.refetch()}
      isEmpty={rows.length === 0}
      emptyHeading={t('admin.overview.status.empty')}
    >
      <Table aria-label={t('admin.overview.status.title')}>
        <TableHead>
          <TableRow>
            <TableHeader>{t('admin.overview.status.status')}</TableHeader>
            <TableHeader className="pos-overview__number">{t('admin.overview.status.count')}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.status}>
              <TableCell>
                <OrderStatusTag status={r.status} />
              </TableCell>
              <TableCell className="pos-overview__number">{format.number(r.count)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </WidgetCard>
  );
}

function SeriesWidget({
  mode,
  title,
  type,
  unit,
  labelHeader,
  series,
  measure = 'sales',
  dayOfMonth = false,
}: {
  mode: OverviewMode;
  title: string;
  type: 'bar' | 'line';
  unit: string;
  labelHeader: string;
  /** What each bucket plots: its net sales (money) or its number of completed orders. */
  measure?: 'sales' | 'orders';
  /** Label buckets by day of the month ("5") instead of their full label, for a month of days. */
  dayOfMonth?: boolean;
  series: { data?: SalesSeriesDto; isPending: boolean; isError: boolean; error: unknown; refetch(): unknown };
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const summary = useSalesSummary(mode);
  const currency = summary.data?.currencyCode;
  const formatValue = useMemo(
    () => (v: number) => (measure === 'sales' && currency ? format.money(v, currency) : format.number(v)),
    [format, currency, measure],
  );
  const points = useMemo(
    () =>
      (series.data?.buckets ?? []).map((b) => ({
        label: dayOfMonth ? String(Number(b.label.slice(8, 10))) : b.label,
        value: measure === 'orders' ? b.orders : b.sales,
      })),
    [series.data, measure, dayOfMonth],
  );
  const total = points.reduce((sum, p) => sum + p.value, 0);
  const peak = points.reduce<(typeof points)[number] | undefined>(
    (best, p) => (!best || p.value > best.value ? p : best),
    undefined,
  );
  return (
    <WidgetCard
      title={title}
      headerExtra={mode === 'ho' ? <LagNotice /> : undefined}
      isLoading={series.isPending}
      isError={series.isError}
      error={series.error}
      onRetry={() => void series.refetch()}
      isEmpty={total === 0}
      emptyHeading={t('admin.overview.sales.empty')}
    >
      <TrendChart
        type={type}
        points={points}
        labelHeader={labelHeader}
        valueHeader={t(measure === 'orders' ? 'admin.overview.month.ordersSeries' : 'admin.overview.sales.seriesName')}
        summary={t(measure === 'orders' ? 'admin.overview.month.ordersSummary' : 'admin.overview.sales.summary', {
          unit,
          total: formatValue(total),
          peak: peak ? `${peak.label} (${formatValue(peak.value)})` : formatValue(0),
        })}
        formatValue={formatValue}
      />
    </WidgetCard>
  );
}

export function SalesByHourWidget({ mode }: { mode: OverviewMode }) {
  const { t } = useTranslation();
  const api = useOverviewApi();
  const series = useOverviewQuery(['hourly', mode], () => api.hourly({ date: todayDate() }), MINUTE);
  return (
    <SeriesWidget
      mode={mode}
      title={t('admin.overview.sales.byHour')}
      type="bar"
      unit={t('admin.overview.sales.hour').toLowerCase()}
      labelHeader={t('admin.overview.sales.hour')}
      series={series}
    />
  );
}

/** Both month widgets read the same query (one request): every day of this month up to today, in the store's days. */
function useThisMonth(mode: OverviewMode) {
  const api = useOverviewApi();
  return useOverviewQuery(['daily-month', mode], () => api.daily({ month: 'current' }), 5 * MINUTE);
}

/** Net sales per day for the current month. */
export function RevenueThisMonthWidget({ mode }: { mode: OverviewMode }) {
  const { t } = useTranslation();
  return (
    <SeriesWidget
      mode={mode}
      title={t('admin.overview.month.revenue')}
      type="bar"
      unit={t('admin.overview.sales.day').toLowerCase()}
      labelHeader={t('admin.overview.sales.day')}
      series={useThisMonth(mode)}
      dayOfMonth
    />
  );
}

/** Completed (paid) orders per day for the current month. */
export function OrdersThisMonthWidget({ mode }: { mode: OverviewMode }) {
  const { t } = useTranslation();
  return (
    <SeriesWidget
      mode={mode}
      title={t('admin.overview.month.orders')}
      type="bar"
      unit={t('admin.overview.sales.day').toLowerCase()}
      labelHeader={t('admin.overview.sales.day')}
      series={useThisMonth(mode)}
      measure="orders"
      dayOfMonth
    />
  );
}

export function PaymentsByProviderWidget({ mode }: { mode: OverviewMode }) {
  const { t } = useTranslation();
  const format = useFormat();
  const api = useOverviewApi();
  const query = useOverviewQuery(['payments', mode], () => api.paymentsByProvider(todayRange()), MINUTE);
  const rows = query.data ?? [];
  return (
    <WidgetCard
      title={t('admin.overview.payments.title')}
      headerExtra={mode === 'ho' ? <LagNotice /> : undefined}
      isLoading={query.isPending}
      isError={query.isError}
      error={query.error}
      onRetry={() => void query.refetch()}
      isEmpty={rows.length === 0}
      emptyHeading={t('admin.overview.payments.empty')}
    >
      <Table aria-label={t('admin.overview.payments.title')}>
        <TableHead>
          <TableRow>
            <TableHeader>{t('admin.overview.payments.provider')}</TableHeader>
            <TableHeader>{t('admin.overview.payments.method')}</TableHeader>
            <TableHeader className="pos-overview__number">{t('admin.overview.payments.count')}</TableHeader>
            <TableHeader className="pos-overview__number">{t('admin.overview.payments.paid')}</TableHeader>
            <TableHeader className="pos-overview__number">{t('admin.overview.payments.refunded')}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.providerCode ?? ''}|${r.methodType}|${r.currencyCode}`}>
              <TableCell>{r.providerName ?? t('transactions.filters.noProvider')}</TableCell>
              <TableCell>
                {t(`orders.methodTypes.${r.methodType}` as I18nKey, { defaultValue: r.methodType })}
              </TableCell>
              <TableCell className="pos-overview__number">{format.number(r.count)}</TableCell>
              <TableCell className="pos-overview__number">{format.money(r.paid, r.currencyCode)}</TableCell>
              <TableCell className="pos-overview__number">{format.money(r.refunded, r.currencyCode)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </WidgetCard>
  );
}
